import type { QueryClient } from '@tanstack/react-query'
import type { LedgerSyncSyncPipeline } from '@dcp/shared/ledgersync/sync-state'
import { BANKING_KEYS, CASH_POSITION_SUMMARIES_KEY } from '@/lib/query-keys'

/**
 * Which caches a LedgerSync sync — or any write to a connection's accounts —
 * can have changed, in ONE place (DCP-324, DCP-401). Shared by the connection
 * hooks, the sync mutations and the completion watcher, which would otherwise
 * each keep a copy of these key lists.
 */

/** How an invalidation treats the matched queries that are mounted. */
type RefetchType = 'active' | 'none'

/**
 * Invalidate every cache a write to a connection's ACCOUNTS can have changed.
 *
 * Facility, account type and the Cash Position flag are read far outside this
 * screen: the Bank Balance tile filters by account type (DCP-371), the Bank
 * Balance Update table lists accounts, Cash Position sums the ones flagged in,
 * and the dashboard's account picker offers them by facility. Leaving those
 * cached is what makes a reassignment look like it did nothing until the 5-minute
 * staleTime expires.
 *
 * @param queryClient - The active query client
 * @param refetchType - `'active'` (default) refetches the matched queries that are
 *   mounted; `'none'` only MARKS them stale, for a write that has not landed yet
 * @returns Settles once the matched active queries have refetched — for a
 *   mutation that should stay pending until the screen shows its result
 */
export function invalidateAccountDependents(
  queryClient: QueryClient,
  refetchType: RefetchType = 'active',
): Promise<void> {
  const invalidations = [
    queryClient.invalidateQueries({ refetchType, queryKey: BANKING_KEYS.allConnectionAccounts }),
    // The list's `accounts_count` is a reduction over these same rows.
    queryClient.invalidateQueries({ refetchType, queryKey: BANKING_KEYS.allBankConnections }),
    // The facility page's per-connection counts reduce the same rows by facility.
    queryClient.invalidateQueries({ refetchType, queryKey: BANKING_KEYS.allFacilityConnectionAccountCounts }),
    queryClient.invalidateQueries({ refetchType, queryKey: BANKING_KEYS.allBankAccounts }),
    queryClient.invalidateQueries({ refetchType, queryKey: BANKING_KEYS.allBalanceImportStatus }),
    queryClient.invalidateQueries({ refetchType, queryKey: BANKING_KEYS.allKpi }),
    queryClient.invalidateQueries({ refetchType, queryKey: CASH_POSITION_SUMMARIES_KEY }),
    // Cross-Checking and LOC Usage belong HERE, in the shared helper, rather than
    // at the individual delete call sites — a deliberate choice, and this is why.
    //
    // LOC Usage's account predicate ("Predicate B" in migration
    // 20260910080000_dcp324_loc_usage_eligible_accounts.sql) is
    // `deleted_at IS NULL AND sync_disabled_at IS NULL AND provider_removed_at IS
    // NULL AND facility_id IS NOT NULL` — i.e. the predicate is written over
    // EXACTLY the columns the mutations below write. Disconnect stamps
    // `sync_disabled_at` (AC#20), deleting a connection soft-deletes the accounts
    // (AC#21), assigning a facility sets/clears `facility_id` (AC#22), and since
    // `transactions.facility_id` was dropped in favour of deriving the facility
    // from the account (20260911000000), a reassignment moves the account's WHOLE
    // transaction history to the other facility retroactively. That is FIVE of the
    // seven mutations in this file (facility, disconnect, reconnect, delete
    // accounts, delete connection); only account type and the Cash Position opt-in
    // sit outside the predicate. A per-call-site copy of these three lines would
    // therefore be a rule to remember five times over, and the sixth time somebody
    // adds a mutation here.
    //
    // Cross-Checking rides along for the same reason from the other direction: it
    // compares those accounts' transactions against their `balances`, and the
    // deletes remove balance rows outright (`delete-connection` hard-deletes
    // them; `permanentlyDelete` deletes transactions and balances both).
    //
    // Cost of the wider fan-out is near zero: none of these three keys is ever
    // mounted on the connection screens, and React Query only REFETCHES active
    // queries — an inactive match is marked stale and re-read the next time its
    // page mounts, which is precisely the behaviour wanted. Leaving them out is
    // what costs: up to the 5-minute staleTime of Cross-Checking and LOC figures
    // computed from rows that no longer exist, with `refetchOnWindowFocus: false`
    // (queryClient.ts) meaning a re-navigation inside that window does not fix it.
    queryClient.invalidateQueries({ refetchType, queryKey: BANKING_KEYS.allCrossChecking }),
    queryClient.invalidateQueries({ refetchType, queryKey: BANKING_KEYS.allLocAvailability }),
    queryClient.invalidateQueries({ refetchType, queryKey: BANKING_KEYS.allLocTransactions }),
    // DCP-374: Credit Card Availability reads the same accounts. Assigning a card
    // to a facility, re-typing it, or disconnecting it all change which rows the
    // RPC returns — and without this the page serves the pre-change answer for
    // the full staleTime (PR #238 review).
    queryClient.invalidateQueries({ refetchType, queryKey: BANKING_KEYS.allCreditCardAvailability }),
    // A card's "View transactions" list: deleting or disconnecting accounts
    // removes their transactions, and the transactions sync builds on this helper.
    queryClient.invalidateQueries({ refetchType, queryKey: BANKING_KEYS.allCreditCardTransactions }),
  ]
  return Promise.all(invalidations).then(() => undefined)
}

/**
 * Invalidate what a finished LedgerSync TRANSACTIONS sync can have changed
 * (DCP-401). Called by the completion watcher when the pipeline goes idle, and
 * at enqueue time with `refetchType: 'none'` — see `markSyncDataStale`.
 *
 * A superset of {@link invalidateAccountDependents}, not a separate list,
 * because the sync writes accounts too: it auto-links accounts the bank newly
 * reports, stamps their sync state and can mark provider-removed ones, which is
 * exactly the account-level change that helper exists for. On top of that go
 * the transaction reads themselves: the source-agnostic transaction lists
 * (`['plaid', 'transactions']`, named for history) and their category list
 * (`['plaid', 'categories']`), as every transaction writer in useBanking.ts
 * does, plus the facility category breakdown that sums them, as a
 * transaction FILE import does (`import-configs.tsx`).
 *
 * @param queryClient - The active query client
 * @param refetchType - See {@link invalidateAccountDependents}
 */
export function invalidateAfterTransactionSync(
  queryClient: QueryClient,
  refetchType: RefetchType = 'active',
): void {
  invalidateAccountDependents(queryClient, refetchType)
  queryClient.invalidateQueries({ refetchType, queryKey: ['plaid', 'transactions'] })
  queryClient.invalidateQueries({ refetchType, queryKey: ['plaid', 'categories'] })
  queryClient.invalidateQueries({ refetchType, queryKey: BANKING_KEYS.allFacilityCategories })
}

/**
 * Invalidate what a finished LedgerSync BALANCES sync can have changed
 * (DCP-401).
 *
 * The balance reader writes `balances` rows and the account-level balance /
 * credit fields (balance, available balance, credit limit, card terms). Every
 * reader of those is already in {@link invalidateAccountDependents} — Balance
 * Import, the KPI tiles, Cash Position, Cross-Checking, LOC and Credit Card
 * Availability, and the accounts table's balance column — so this is that set,
 * named for the event so the watcher reads as what it means.
 *
 * @param queryClient - The active query client
 * @param refetchType - See {@link invalidateAccountDependents}
 */
export function invalidateAfterBalanceSync(
  queryClient: QueryClient,
  refetchType: RefetchType = 'active',
): void {
  invalidateAccountDependents(queryClient, refetchType)
}

/**
 * Mark the data a just-ENQUEUED sync will change as stale, without re-reading
 * it (DCP-401).
 *
 * Nothing has been written yet, so refetching now would only show pre-sync
 * rows again — that is what the enqueue-time invalidation this replaces did.
 * But the completion watcher lives on the Settings screens only: leave them
 * and the finish is never seen, and a dashboard opened a few minutes later
 * would serve the pre-sync cache for the rest of the 5-minute staleTime.
 * Marking stale makes that next mount refetch; the watcher still does the real
 * refresh when the run ends.
 *
 * @param queryClient - The active query client
 * @param pipelines - The pipelines the click started
 */
export function markSyncDataStale(
  queryClient: QueryClient,
  pipelines: readonly LedgerSyncSyncPipeline[],
): void {
  if (pipelines.includes('transactions')) invalidateAfterTransactionSync(queryClient, 'none')
  else if (pipelines.includes('balances')) invalidateAfterBalanceSync(queryClient, 'none')
}
